import React, { Component } from 'react'
import { Segment, Label, Table } from 'semantic-ui-react'
import Enumerable from 'linq'

class SpecWidgetTab extends Component {
  renderParameterDescription (description) {
    return description !== undefined && description.startsWith('http')
      ? (<a href={description} target='_blank'>Go to external docs <i className='fa fa-external-link' /></a>)
      : (<div>            
          {description.split('\n').map((i, key) => {
            return <div key={key}>{i}</div>
          })}
        </div>)
  }

  renderAnalytics (parameter) {
    return (
      <tr key={parameter.name}>
        <td className='three wide'>
          <span>{parameter.name}</span>
        </td>
        <td className='one wide'>
          <Label size='tiny'>{parameter.in}</Label>
        </td>
        <td className='one wide'>
          <span className='doc-w-param-datatype' ><Label size='tiny'>{this.renderType(parameter)}</Label>
          </span>
        </td>
        <td className='eleven wide' >
          <span className='doc-w-param-value'>{this.renderParameterDescription(parameter.description)}</span>
          {parameter.example && (
            <code>{JSON.stringify(parameter.example)}</code>
          )}
        </td>
      </tr>
    )
  }

  renderUser (parameter) {
    return (
      <tr key={parameter.name}>
        <td className='three wide'>
          <span>{parameter.name}</span>
        </td>
        <td className='one wide'>
          {this.props.operation.spec.operationId === 'GetCAD' 
          ? <Label size='tiny'>formData</Label> 
          : <Label size='tiny'>{parameter.in}</Label>}
        </td>
        <td className='one wide'>
          <span className='doc-w-param-datatype' ><Label size='tiny'>{this.renderType(parameter)}</Label>
          </span>
        </td>
        <td className='eleven wide' >
          <span className='doc-w-param-value'>{this.renderParameterDescription(parameter.description)}</span>
          {parameter.example && (
            <code>{JSON.stringify(parameter.example)}</code>
          )}
        </td>
      </tr>
    )
  }

  renderParameter (parameter) {
    return (
      <tr key={parameter.name}>
        <td className='three wide'>
          <span>{parameter.name}</span>
          <span title='Required field'>{parameter.required ? '*' : ''}</span>
        </td>
        <td className='one wide'>
          <Label size='tiny'>{this.getParameterType(parameter)}</Label>
        </td>
        <td className='one wide'>
          <span className='doc-w-param-datatype' ><Label size='tiny'>{this.renderType(parameter)}</Label>
          </span>
        </td>
        <td className='eleven wide' >
          <span className='doc-w-param-value'>{this.renderParameterDescription(parameter.description)}</span>
          {parameter.example && (
            <code>{JSON.stringify(parameter.example)}</code>
          )}
        </td>
      </tr>
    )
  }
  
  getParameterType(parameter) {
    // force it to 'path' if the special 'x-tdxpathparameter' parameter is defined as true on the parameter definition.
    // Even though they are 'path' parameters (primarily found on the hosted catalog services APIs), they are most
    // likely defined as 'query' parameters within the parameter definition because of the following issues if we
    // attempted to define them as 'path' on the parameter definition:
    // - OpenAPI doesn't allow optional 'path' parameters (which some of our API path parameters are)
    // - it also changes the display UI around, making it inconsistent with our other mostly query only parameter APIs
    // - would have to rewrite some of the special handling for those APIs that have path parameters (mostly
    //   the hosted catalog APIs) because some of the parameters are optional
    // so we'll just simply change the display to 'path' and leave everything else the same
    return parameter['x-tdxpathparameter'] ? 'path' : parameter.in;
  }

  renderType (parameter) {
    if (parameter.type) {
      return <abbr>{parameter.type}</abbr>
    }

    if (parameter.schema) {
      return (
        <div>
          <abbr>{parameter.schema.type}</abbr>
        </div>
      )
    }

    return <span>-</span>
  }

  renderStatusCodes (statusCode, description) {
    return (
      <tr key={statusCode}>
        <td className='one wide' ><Label size='tiny'>{statusCode}</Label></td>
        <td className='fifteen wide' >{description}</td>
      </tr>)
  }

  renderDefinitions () {
    if (!this.props.operation.spec.parameters) return null

    const definitions = Enumerable.from(this.props.operation.spec.parameters)
      .where(parameter => parameter.schema && parameter.schema.$ref)
      .select(parameter => parameter.schema.$ref)
      .distinct()
      .select($ref => ({ $ref: $ref, schema: this.props.definitions[$ref] }))
      .toArray()

    return (
      definitions.length > 0 && <Segment>
        {definitions.map(definition => (
          <div key={definition.$ref}>
            <h5>{definition.$ref}</h5>
            <pre>{JSON.stringify(definition.schema, null, 2)}</pre>
          </div>
        ))}
      </Segment>)
  }

  render () {
    if (!this.props.operation) {
      return (<div>Operation not found</div>)
    }

    let showUser = false
    let showAnalytics = false
    if (this.props.operation.spec.operationId === 'ComboViewer'
      || this.props.operation.spec.operationId === 'ProductLite'
      || this.props.operation.spec.operationId === 'Catalog'
      || this.props.operation.spec.operationId === 'Product'
      || this.props.operation.spec.operationId === 'GetCAD'
      || this.props.operation.spec.operationId === 'GetCADStatus'
      || this.props.operation.spec.operationId === 'GetProductPreview') {
        showUser = true
    }
    if (this.props.operation.spec.operationId === 'AnalyticsNumbers'
      || this.props.operation.spec.operationId === 'Analytics') {
      showAnalytics = true
    }

    const user = [
      {name: 'UserEmail', in: 'query', type: 'string', description: 'The requesting user\'s email address.'}, 
      {name: 'UserFirstName', in: 'query', type: 'string', description: 'The requesting user\'s first name.'}, 
      {name: 'UserLastName', in: 'query', type: 'string', description: 'The requesting user\'s last name.'}, 
      {name: 'UserCompany', in: 'query', type: 'string', description: 'The requesting user\'s company name.'},
      {name: 'UserCompanySize', in: 'query', type: 'string', description: 'The requesting user\'s company size.'},
      {name: 'UserAddress', in: 'query', type: 'string', description: 'The requesting user\'s primary address.'},
      {name: 'UserAddress2', in: 'query', type: 'string', description: 'The requesting user\'s secondary address.'},
      {name: 'UserAddress3', in: 'query', type: 'string', description: 'The requesting user\'s tertiary address.'},
      {name: 'UserCity', in: 'query', type: 'string', description: 'The requesting user\'s city.'},
      {name: 'UserPostalCode', in: 'query', type: 'string', description: 'The requesting user\'s postal code.'},
      {name: 'UserCountry', in: 'query', type: 'string', description: 'The requesting user\'s country.'},
      {name: 'UserCountryCode', in: 'query', type: 'string', description: 'The requesting user\'s country code. This should be the ISO two character country code. If you do not have the ISO code leave this field blank.'},
      {name: 'UserState', in: 'query', type: 'string', description: 'The requesting user\'s state/province.'}, 
      {name: 'UserPhone', in: 'query', type: 'string', description: 'The requesting user\'s phone number.'},
      {name: 'UserIndustry', in: 'query', type: 'string', description: 'The requesting user\'s industry.'},
      {name: 'UserDepartment', in: 'query', type: 'string', description: 'The requesting user\'s department.'},
      {name: 'UserJobFunction', in: 'query', type: 'string', description: 'The requesting user\'s job function.'},
      {name: 'UserLanguage', in: 'query', type: 'string', description: 'The requesting user\'s language.'}, 
      {name: 'DataSource', in: 'query', type: 'string', description: 'The name of the application requesting the data. For example, if the hosted catalog service is part of your company web site, then provide \"<company name> Website\" as the value, where <company name> is your company name.'}, 
      {name: 'UserAgent', in: 'query', type: 'string', description: 'The client\'s User Agent or Browser Identification.'},
    ]

    const analytics = [
      {name: 'CADFormat', in: 'query', type: 'array', description: 'If set, only returns analytic records that match this cad format ID value, else returns all records that match any cad format ID value. Valid CAD format IDs can be retrieved via the response of the GetAnalyticsCADFormats API call.'}, 
      {name: 'Email', in: 'query', type: 'array', description: 'If set, only returns analytic records that match this email address, else returns all records that match any email address.'}, 
      {name: 'Origin', in: 'query', type: 'array', description: 'If set, only returns analytic records that match this origin value, else returns all records that match any origin value.'}, 
      {name: 'Language', in: 'query', type: 'array', description: 'If set, only returns analytic records that match this language value, else returns all records that match any language value. Valid values should be the language code, for example en, fr, de etc.'}, 
      {name: 'PartNumber', in: 'query', type: 'array', description: 'If set, only returns analytic records that match this PartNumber value, else returns all records that match any PartNumber value.'}, 
      {name: 'Status', in: 'query', type: 'array', description: 'If set, only returns analytic records that match this status value, else returns all records that match any status value.'}, 
      {name: 'City', in: 'query', type: 'array', description: 'If set, only returns analytic records that match this city value, else returns all records that match any city value.'}, 
      {name: 'State', in: 'query', type: 'array', description: 'If set, only returns analytic records that match this state value, else returns all records that match any state value.'}, 
      {name: 'Country', in: 'query', type: 'array', description: 'If set, only returns analytic records that match this ISO country code value, else returns all records that match any ISO country code value.'}, 
      {name: 'Company', in: 'query', type: 'array', description: 'If set, only returns analytic records that match this company value, else returns all records that match any company value.'}, 
      {name: 'CompanySize', in: 'query', type: 'array', description: 'If set, only returns analytic records that match this companySize value, else returns all records that match any companySize value.'}]

    return (
      <Segment attached='bottom' >
        <div>
          <p>{this.props.operation.spec.summary}</p>
          <div>
            {this.props.operation.spec.description.split('\n').map((i, key) => {
              return <div key={key}>{i}</div>
            })}
          </div>
          {/* {this.props.operation.spec.description} */}
          <Segment>
            <h4>Parameters</h4>
            <Table>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell content='Name' />
                  <Table.HeaderCell content='Type' />
                  <Table.HeaderCell content='Data' />
                  <Table.HeaderCell content='Description' />
                </Table.Row>
              </Table.Header>
              <Table.Body className='operation-params'>
                {this.props.operation.spec.parameters 
                  && (this.props.operation.spec.operationId !== 'AnalyticsNumbers' && this.props.operation.spec.operationId !== 'Analytics')
                  && this.props.operation.spec.parameters.filter(parameter => {
                    return user.every(user => parameter.name != user.name)
                  }).map(parameter => this.renderParameter(parameter))
                }
                {this.props.operation.spec.parameters 
                  && (this.props.operation.spec.operationId === 'AnalyticsNumbers' || this.props.operation.spec.operationId === 'Analytics')
                  && this.props.operation.spec.parameters.filter(parameter => {     
                    return analytics.every(analytics => parameter.name != analytics.name)
                  }).map(parameter => this.renderParameter(parameter))}
              </Table.Body>
            </Table>
            {/* <table className='ui table table-striped' >
              <tbody className='operation-params' >
                {this.props.operation.spec.parameters && this.props.operation.spec.parameters.map(parameter =>
                  this.renderParameter(parameter)
                )}
              </tbody>
            </table> */}
          </Segment>
          {showUser && <Segment>
            <h4>User Data Parameters</h4>
            <p style={{margin: 0}}>We suggest that the following parameters be sent with each request - this will benefit your analytic reports.</p>
            <Table>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell content='Name' />
                  <Table.HeaderCell content='Type' />
                  <Table.HeaderCell content='Data' />
                  <Table.HeaderCell content='Description' />
                </Table.Row>
              </Table.Header>
              <Table.Body className='operation-params'>
                {user.map(user => 
                  this.renderUser(user)
                )}
              </Table.Body>
            </Table>
          </Segment>}
          {showAnalytics && <Segment>
            <h4>Criteria Parameters</h4>
            <p style={{margin: 0}}>
              The following are optional criteria parameters which limit the returned analytic records to the specified criteria parameter values.
              {this.props.operation.spec.operationId === 'AnalyticsNumbers' && 
                <p>
                  <br/>
                  Multiple values can be specified for any criteria parameter by using the array annotation ([]) format on the URL for the criteria parameter. 
                  For example, to specify 3 criteria values (PN65932, PN50034 and PN22310) for PartNumber, you would format the API URL as: <br /> <br />
                  https://api.3dexchange.net/AnalyticsNumbers?APIKey=123&Catalog=987654&PartNumber[]=PN65932&PartNumber[]=PN50034&PartNumber[]=PN22310
                </p>
              }
            </p>
            <Table>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell content='Name' />
                  <Table.HeaderCell content='Type' />
                  <Table.HeaderCell content='Data' />
                  <Table.HeaderCell content='Description' />
                </Table.Row>
              </Table.Header>
              <Table.Body className='operation-params'>
                {analytics.map(analytics => 
                  this.renderAnalytics(analytics)
                )}
              </Table.Body>
            </Table>
          </Segment>}
          {/* <Segment>
            <h4>Response Status Codes</h4>
            <Table striped>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell content='Response' />
                  <Table.HeaderCell content='Description' />
                </Table.Row>
              </Table.Header>
              <Table.Body className='operation-status'>
                {Object.keys(this.props.operation.spec.responses).map(statusCode =>
                  this.renderStatusCodes(
                    statusCode,
                    this.props.operation.spec.responses[statusCode].description
                  )
                )}
              </Table.Body>
            </Table>
          </Segment> */}
          {this.renderDefinitions()}
        </div>
      </Segment>
    )
  }
}

export default SpecWidgetTab



// WEBPACK FOOTER //
// ./src/components/widgets/SpecWidgetTab.js